import { inject, Injectable } from '@angular/core';
import { filter, map, Observable, pipe, switchMap } from 'rxjs';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ComponentType } from '@angular/cdk/portal';

type PsDialogServiceChainParams<T> = {
  component: ComponentType<DynamicDialog<T>>;
  options?: DynamicDialogConfig<unknown, object>;
  shouldProceed?: (result: T) => boolean;
  after?: (result: T) => T;
};

// TODO: use common interface
// eslint-disable-next-line unused-imports/no-unused-vars
export type DynamicDialog<T> = object;

@Injectable({
  providedIn: 'root',
})
export class PsDialogService {
  private readonly dialogService = inject(DialogService);

  open<T>(
    componentType: ComponentType<DynamicDialog<T>>,
    options?: DynamicDialogConfig<unknown, object>,
  ) {
    const ref = this.dialogService.open(componentType, {
      modal: true,
      showHeader: false,
      ...options,
    });
    // TODO: determine how a proper result object could look like
    return ref!.onClose.pipe(map((result) => result as T));
  }

  chain<T, A = boolean, B = T>(
    componentA: PsDialogServiceChainParams<A>,
    componentB: PsDialogServiceChainParams<B>,
  ): Observable<T>;
  chain<T, A = boolean, B = boolean, C = T>(
    componentA: PsDialogServiceChainParams<A>,
    componentB: PsDialogServiceChainParams<B>,
    componentC: PsDialogServiceChainParams<C>,
  ): Observable<C>;
  chain<T, A = boolean, B = boolean, C = boolean, D = T>(
    componentA: PsDialogServiceChainParams<A>,
    componentB: PsDialogServiceChainParams<B>,
    componentC: PsDialogServiceChainParams<C>,
    componentD: PsDialogServiceChainParams<D>,
  ): Observable<D>;
  chain<T>(...componentTypes: PsDialogServiceChainParams<T>[]): Observable<T> {
    if (componentTypes.length === 0) {
      throw new Error('No components provided');
    }
    if (componentTypes.length === 1) {
      return this.open(componentTypes[0].component, componentTypes[0].options);
    }

    // we have to call this initially to correctly infer the type
    let chainedDialogPipe = () => pipe(this.buildOperators(componentTypes[1]));
    for (let i = 2; i < componentTypes.length - 1; i++) {
      chainedDialogPipe = () => pipe(chainedDialogPipe(), this.buildOperators(componentTypes[i]));
    }
    return this.open<T>(componentTypes[0].component, componentTypes[0].options).pipe(
      map((result) => {
        return componentTypes[0].after?.(result) ?? result;
      }),
      filter(componentTypes[0].shouldProceed ?? Boolean),
      chainedDialogPipe(),
      map((result) => result as T),
    );
  }

  private buildOperators<T>(componentType: PsDialogServiceChainParams<T>) {
    return pipe(
      switchMap(() => this.open<T>(componentType.component, componentType.options)),
      map((result) => {
        return componentType.after?.(result) ?? result;
      }),
      filter(componentType.shouldProceed ?? Boolean),
    );
  }
}
